import { useRef, useState } from 'react';
import { Alert, Platform } from 'react-native';
import { useAuthStore } from '../store/authStore';
import { usePostsStore } from '../store/postsStore';
import { useUIStore } from '../store/uiStore';
import { useOutboxStore } from '../store/outboxStore';
import { useConfirm } from '../components/common';
import type {
  PostLocation,
  PostLocationSectionHandle,
  SelectedChecklistItem,
} from '../components/post-create';
import type { Post, PostCategory, PostVisibility } from '../types';
import type { CreatePostData } from '../types/api';
import type { OutboxObservationData } from '../api/outbox/types';
import { OUTBOX_ENABLED } from '../constants/outbox';
import { isOnline } from '../utils/network';
import { isNetworkError } from '../utils/errorHandler';
import { captureAppError } from '../utils/sentry';
import { sharePost } from '../utils/share';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('post:create');

// Only the direct (online, non-outbox) submit attempt is bounded — an
// enqueue never starts an upload, so it can't hang the same way. Timeout
// message deliberately contains "timed out" so isNetworkError's message
// check also recognizes it, in case a caller checks that alone.
const SUBMIT_TIMEOUT_MS = 20_000;

class SubmitTimeoutError extends Error {}

function withSubmitTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new SubmitTimeoutError(`Post submit timed out after ${ms}ms`)),
      ms
    );
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

/** What happened, and (for the two paths that can link a structure) which
 *  one — the caller (a route or a docked panel) decides what to do next;
 *  this hook never navigates itself. See each call site below for exactly
 *  which router call this replaces. */
export type PostSubmitOutcome =
  | { kind: 'updated' }
  | { kind: 'newsPosted' }
  | { kind: 'queued'; structureId?: string }
  | { kind: 'created'; structureId?: string };

export interface UsePostSubmitOptions {
  isEditing: boolean;
  editPostId?: string;
  caption: string;
  newsTitle: string;
  observationType: PostCategory;
  checklistItem: SelectedChecklistItem | null;
  visibility: PostVisibility;
  isAnonymous: boolean;
  userLocation: PostLocation | null;
  /** Baseline for the edit flow's location diff at save time — owned by the
   *  caller (set in its edit-load effect), read here at submit time. */
  editInitialLocationRef: React.RefObject<PostLocation | null>;
  imageUri: string | null;
  linkedStructureId: string | undefined;
  beforePost: Post | null;
  beforeImageUri: string | null;
  /** The caller's PostLocationSection ref — confirmMissingLocation opens its
   *  picker when the author declines to post without a location. */
  locationSectionRef: React.RefObject<PostLocationSectionHandle | null>;
  /** Called once the post is saved/queued (or updated). Replaces every
   *  `router.*` call the inline version used to make directly. */
  onDone: (outcome: PostSubmitOutcome) => void;
}

/**
 * The observation composer's submit path: direct upload with a timeout,
 * outbox enqueue when offline or the direct attempt fails, and the post
 * update path for edits. Moved verbatim out of app/post/create.tsx
 * (panel-system.md Phase 3) — every statement, log line, error message,
 * the idempotent postId scheme and the enqueue payload are unchanged. The
 * only edit is that the `router.back()`/`router.replace()` calls that used
 * to fire directly are now `onDone(...)` calls the caller (the route, or
 * the desktop dock) turns into its own navigation.
 */
export function usePostSubmit(options: UsePostSubmitOptions) {
  const {
    isEditing,
    editPostId,
    caption,
    newsTitle,
    observationType,
    checklistItem,
    visibility,
    isAnonymous,
    userLocation,
    editInitialLocationRef,
    imageUri,
    linkedStructureId,
    beforePost,
    beforeImageUri,
    locationSectionRef,
    onDone,
  } = options;

  const isNews = observationType === 'news';

  const { user } = useAuthStore();
  const { updatePost, uploadAndCreatePost, createPost } = usePostsStore();
  const flagShowSaveAccountToast = useUIStore((s) => s.flagShowSaveAccountToast);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const confirm = useConfirm();

  // Set once the author has answered "post anyway" to the no-location prompt.
  // A ref, not state: it's read inside handleSubmit and must not re-render the
  // screen mid-submit. Glenn posts ~70 observations a day, so asking every
  // time would be its own bug.
  const skipLocationConfirmRef = useRef(false);

  /**
   * A post with no location draws no pin, shows no address and can't be found
   * on the map — and the submit button is deliberately never disabled, so
   * nothing used to stop one going out. Ask once, then take the author's word
   * for the rest of the session.
   *
   * Returns false when the author chose to set a location instead, which
   * cancels the submit. Edits and structure-linked posts skip it: the linked
   * structure's centroid is the location.
   */
  const confirmMissingLocation = async (): Promise<boolean> => {
    if (userLocation || linkedStructureId || skipLocationConfirmRef.current) return true;
    const postAnyway = await confirm({
      title: 'Post without a location?',
      message: "This observation won't appear on the map.",
      confirmLabel: 'Post anyway',
      cancelLabel: 'Set location',
    });
    if (!postAnyway) {
      locationSectionRef.current?.openPicker();
      return false;
    }
    skipLocationConfirmRef.current = true;
    return true;
  };

  const handleSubmit = async () => {
    if (isEditing) {
      if (!editPostId || isSubmitting || !user) return;
      setIsSubmitting(true);
      try {
        const initialLocation = editInitialLocationRef.current;
        const locationChanged =
          userLocation &&
          (!initialLocation ||
            userLocation.latitude !== initialLocation.latitude ||
            userLocation.longitude !== initialLocation.longitude);

        await updatePost(editPostId, {
          caption: caption.trim(),
          // Empty, or the category moved away from news: clear the field.
          title: isNews && newsTitle.trim() ? newsTitle.trim() : null,
          category: observationType,
          // null clears a previously-set link (deleteField under the hood).
          checklistItemId: checklistItem?.id ?? null,
          checklistItemLabel: checklistItem?.label ?? null,
          visibility,
          isAnonymous,
          // Author is always the current user on edit; identity built the
          // same way create's uploadAndCreatePost call does below. A named
          // author's edit refreshes authorName to the current profile.
          ...(isAnonymous
            ? { authorName: '', authorAvatarUrl: null }
            : {
                authorName: `${user.firstName} ${user.lastName}`,
                authorAvatarUrl: user.avatarUrl || null,
              }),
          // Strip undefined like create does — Firestore rejects it.
          // The top-level `address` moves with the pin: create writes it, and
          // leaving it behind here left the detail header reading "Progress
          // observation" after an author fixed a missing location.
          ...(locationChanged && userLocation
            ? {
                location: {
                  latitude: userLocation.latitude,
                  longitude: userLocation.longitude,
                  ...(userLocation.address ? { address: userLocation.address } : {}),
                  ...(userLocation.accuracyM != null ? { accuracyM: userLocation.accuracyM } : {}),
                },
                address: userLocation.address ?? null,
              }
            : {}),
        });
        onDone({ kind: 'updated' });
      } catch (error) {
        log.error('Update post failed:', error);
        captureAppError(error, { stage: 'update', postId: editPostId });
        setIsSubmitting(false);
        notifyFailure(`Failed to update post. Please try again.${errorDetail(error)}`);
      }
      return;
    }

    // Per client plan: Fire Scouts can post without picking a structure.
    // Address is auto-attached from GPS when available; structure linking
    // is encouraged but optional.
    if (isSubmitting || !user) return;
    if (!imageUri && !isNews) {
      notifyFailure('Add a photo before posting your observation.', 'Photo required');
      return;
    }
    // News has no photo requirement, so the words are the post.
    if (isNews && !caption.trim()) {
      notifyFailure('Write your news before posting.', 'Text required');
      return;
    }
    // Asked before isSubmitting flips, so answering "Set location" leaves the
    // composer usable rather than stuck behind a spinner. News is open-ended
    // (no location section), so it never asks.
    if (!isNews && !(await confirmMissingLocation())) return;
    setIsSubmitting(true);

    // Mirrored out here so the catch can report it. The id is also the
    // Storage path segment, which is what let us match orphaned uploads to
    // failed submits when diagnosing the July 2026 outage.
    let submittedPostId: string | undefined;

    try {
      // Base36 timestamp + random suffix: collision-resistant across
      // double-submits (unlike a raw Date.now(), which two rapid taps can
      // share), still matches storage.ts's path allowlist
      // ([A-Za-z0-9_.-]) and doubles as the Firestore post doc id.
      const postId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
      submittedPostId = postId;
      const storagePath = `posts/${user.id}/${postId}.jpg`;

      // Strip undefined fields that Firestore rejects.
      const location = userLocation
        ? {
            latitude: userLocation.latitude,
            longitude: userLocation.longitude,
            ...(userLocation.address ? { address: userLocation.address } : {}),
            // Only a GPS fix carries this; a hand-placed pin has no accuracy
            // to report. Stored so a coarse pin stays identifiable later —
            // no post written before 2026-07-31 has it.
            ...(userLocation.accuracyM != null ? { accuracyM: userLocation.accuracyM } : {}),
          }
        : null;

      const wasAnonymousFirstPost = user.isAnonymous === true && (user.postsCount ?? 0) === 0;

      // Built once — this is what uploadAndCreatePost takes on the direct
      // path, and (with beforePostCreatedAt serialized to ms) what the
      // outbox stores for a replay. Image-derived fields aren't known yet
      // either way.
      const postData: Omit<CreatePostData, 'imageUrl' | 'imagePath' | 'thumbnailPath'> = {
        authorId: user.id,
        authorName: `${user.firstName} ${user.lastName}`,
        authorAvatarUrl: user.avatarUrl || undefined,
        isAnonymous,
        category: observationType,
        caption,
        ...(isNews && newsTitle.trim() ? { title: newsTitle.trim() } : {}),
        location,
        // Per client plan, posters can't hide location anymore; the field
        // stays on the schema only for back-compat reads of older docs.
        locationHidden: false,
        visibility,
        address: userLocation?.address,
        // News is open-ended for now (client ask): no structure, checklist,
        // or before/after links, even if params seeded them before the type
        // was switched to news.
        ...(isNews
          ? {}
          : {
              structureId: linkedStructureId,
              checklistItemId: checklistItem?.id,
              checklistItemLabel: checklistItem?.label,
              // Before/after pair — image and date snapshotted so feed cards
              // render the pair without fetching the linked post.
              beforePostId: beforePost?.id,
              beforePostImageUrl: beforePost?.imageUrl,
              beforePostThumbnailUrl: beforePost?.thumbnailUrl,
              beforePostCreatedAt: beforePost?.createdAt,
            }),
      };

      // Text-only news: a doc-only write, no Storage upload. The outbox
      // exists because uploads hang offline — a plain write has no upload
      // leg, so offline just gets told plainly instead of queued.
      if (isNews && !imageUri) {
        if (OUTBOX_ENABLED && !isOnline()) {
          setIsSubmitting(false);
          notifyFailure('You are offline. Reconnect and post again.', 'No connection');
          return;
        }
        const created = await createPost(postData, postId);
        if (visibility === 'public') {
          void sharePost({ postId: created.id, caption: created.caption }).then((result) => {
            if (result === 'copied') useUIStore.getState().showToast({ message: 'Link copied' });
            if (result === 'failed')
              useUIStore.getState().showToast({ message: "Couldn't copy the link" });
          });
        }
        if (wasAnonymousFirstPost) flagShowSaveAccountToast();
        onDone({ kind: 'newsPosted' });
        return;
      }
      // Past the news branch a photo always exists (the submit gates above);
      // the check re-narrows imageUri for the upload/outbox paths below.
      if (!imageUri) return;

      // Same postId/storagePath as the direct attempt below — createPost is
      // idempotent on postId, so a late-landing direct write and an outbox
      // replay converge on one doc.
      const enqueue = () => {
        const { beforePostCreatedAt, ...rest } = postData;
        const outboxData: OutboxObservationData = {
          ...rest,
          ...(beforePostCreatedAt ? { beforePostCreatedAtMs: beforePostCreatedAt.toMillis() } : {}),
        };
        return useOutboxStore.getState().enqueue({
          postId,
          authorId: user.id,
          imageUri,
          storagePath,
          data: outboxData,
          beforeImageUri: beforeImageUri ?? undefined,
        });
      };

      // No server doc exists yet on this path, so skip sharePost — there's
      // nothing to share until the outbox replays it.
      const finishQueued = () => {
        if (wasAnonymousFirstPost) flagShowSaveAccountToast();
        notifyQueued("Saved. It will upload when you're back online.");
        onDone({ kind: 'queued', structureId: linkedStructureId });
      };

      // A queue failure must never masquerade as a failed write. enqueue()
      // does its own compress / blob re-fetch / IndexedDB work, any of which
      // can throw on its own (iOS Safari evicts IndexedDB under storage
      // pressure), and both call sites sit inside the catch below — so an
      // unguarded throw would show the generic "failed to create" message
      // with nothing actually queued.
      const enqueueOrReport = async (): Promise<boolean> => {
        try {
          await enqueue();
          return true;
        } catch (enqueueError) {
          log.error('Outbox enqueue failed:', enqueueError);
          captureAppError(enqueueError, { stage: 'enqueue', postId });
          setIsSubmitting(false);
          notifyFailure(
            `Could not save your observation to upload later. Your device may be out of storage.${errorDetail(enqueueError)}`,
            'Not saved'
          );
          return false;
        }
      };

      if (OUTBOX_ENABLED && !isOnline()) {
        // Never start an upload with no signal — uploadBytesResumable
        // doesn't fail fast offline, it just hangs.
        if (await enqueueOrReport()) finishQueued();
        return;
      }

      let created: Post;
      try {
        // uploadAndCreatePost handles the Storage upload, thumbnail path
        // prediction, and feed/structure optimistic prepend.
        const directCreate = uploadAndCreatePost(
          imageUri,
          storagePath,
          postData,
          // Self-contained before photo (no prior post required).
          beforeImageUri ?? undefined
        );
        created = await (OUTBOX_ENABLED
          ? withSubmitTimeout(directCreate, SUBMIT_TIMEOUT_MS)
          : directCreate);
      } catch (directError) {
        if (
          OUTBOX_ENABLED &&
          (directError instanceof SubmitTimeoutError || isNetworkError(directError))
        ) {
          if (await enqueueOrReport()) finishQueued();
          return;
        }
        throw directError;
      }

      // Per client plan: encourage scouts to share posts they make. Public
      // only — private/insurance-only posts shouldn't auto-prompt sharing.
      if (visibility === 'public') {
        void sharePost({ postId: created.id, caption: created.caption }).then((result) => {
          if (result === 'copied') useUIStore.getState().showToast({ message: 'Link copied' });
          if (result === 'failed')
            useUIStore.getState().showToast({ message: "Couldn't copy the link" });
        });
      }

      // Anonymous scouts: flag the map's "save your account" toast so it
      // appears when they land back on the map. The toast respects the
      // persisted "Not now" dismissal, so this is safe to set on every
      // first anonymous post.
      if (wasAnonymousFirstPost) flagShowSaveAccountToast();

      onDone({ kind: 'created', structureId: linkedStructureId });
    } catch (error) {
      log.error('Create post failed:', error);
      captureAppError(error, { stage: 'create', postId: submittedPostId });
      setIsSubmitting(false);
      notifyFailure(`Failed to create post. Please try again.${errorDetail(error)}`);
    }
  };

  return { isSubmitting, handleSubmit };
}

// A Firebase/AppError code appended to the user-facing message. It costs the
// reader nothing and turns a screenshot into a diagnosis: the July 2026
// outage was a permission-denied that the bare message hid completely.
function errorDetail(error: unknown): string {
  const code = (error as { code?: unknown })?.code;
  return typeof code === 'string' && code ? ` (${code})` : '';
}

function notifyFailure(message: string, title = 'Error'): void {
  if (Platform.OS === 'web') {
    window.alert(message);
  } else {
    Alert.alert(title, message);
  }
}

// Same non-blocking-as-this-codebase-gets pattern as notifyFailure, for the
// offline-queued success path (see OUTBOX_ENABLED in handleSubmit).
function notifyQueued(message: string, title = 'Saved'): void {
  if (Platform.OS === 'web') {
    window.alert(message);
  } else {
    Alert.alert(title, message);
  }
}
